import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'wouter';
import {
  trackPageView,
  trackButtonClick,
  trackLinkClick,
  trackScrollDepth,
  trackOutboundLink,
  trackFormSubmit,
} from '@/lib/analytics';
import {
  trackPixelPageView,
  trackPixelLead,
  trackPixelContact,
} from '@/lib/meta-pixel';

export function usePageTracking() {
  const [location] = useLocation();
  const prevLocation = useRef(location);
  const initialFired = useRef(false);

  useEffect(() => {
    if (prevLocation.current !== location) {
      trackPageView(location);
      trackPixelPageView();
      prevLocation.current = location;
    }
  }, [location]);

  useEffect(() => {
    if (initialFired.current) return;
    initialFired.current = true;
    trackPageView(location);
    // Note: Initial Meta Pixel PageView is fired by the inline script in index.html
    // on first load, so we don't fire it again here to avoid double-counting.
  }, []);
}

export function useScrollTracking() {
  const [location] = useLocation();
  const trackedDepths = useRef<Set<number>>(new Set());

  useEffect(() => {
    trackedDepths.current = new Set();
  }, [location]);

  useEffect(() => {
    const thresholds = [25, 50, 75, 90, 100];

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;

      thresholds.forEach((threshold) => {
        if (scrollPercent >= threshold && !trackedDepths.current.has(threshold)) {
          trackedDepths.current.add(threshold);
          trackScrollDepth(threshold, location);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location]);
}

export function useClickTracking() {
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const clickableElement = target.closest('button, a, [role="button"], [data-testid]');
      
      if (!clickableElement) return;

      const testId = clickableElement.getAttribute('data-testid');
      const tagName = clickableElement.tagName.toLowerCase();

      if (tagName === 'button' || clickableElement.getAttribute('role') === 'button') {
        const buttonText = clickableElement.textContent?.trim().slice(0, 50) || 'unnamed';
        const location = getElementLocation(clickableElement);
        trackButtonClick(testId || buttonText, location);
      }

      if (tagName === 'a') {
        const anchor = clickableElement as HTMLAnchorElement;
        const linkText = anchor.textContent?.trim().slice(0, 50) || 'unnamed';
        const href = anchor.href;
        const location = getElementLocation(anchor);

        if (href && !href.startsWith(window.location.origin) && !href.startsWith('/')) {
          trackOutboundLink(href);
        }

        trackLinkClick(testId || linkText, href, location);
      }
    };

    document.addEventListener('click', handleClick, { capture: true });
    return () => document.removeEventListener('click', handleClick, { capture: true });
  }, []);
}

function getElementLocation(element: Element): string {
  const header = element.closest('header');
  if (header) return 'header';

  const footer = element.closest('footer');
  if (footer) return 'footer';

  const nav = element.closest('nav');
  if (nav) return 'navigation';

  const sidebar = element.closest('[data-sidebar]');
  if (sidebar) return 'sidebar';

  const modal = element.closest('[role="dialog"]');
  if (modal) return 'modal';

  const card = element.closest('[data-card]');
  if (card) return 'card';

  const hero = element.closest('[data-hero]');
  if (hero) return 'hero';

  return 'main';
}

const LEAD_CONNECTOR_HOSTS = [
  'leadconnectorhq.com',
  'msgsndr.com',
  'gohighlevel.com',
];

function isLeadConnectorOrigin(origin: string): boolean {
  if (!origin) return false;
  try {
    const { hostname, protocol } = new URL(origin);
    if (protocol !== 'https:' && protocol !== 'http:') return false;
    return LEAD_CONNECTOR_HOSTS.some(
      (host) => hostname === host || hostname.endsWith(`.${host}`),
    );
  } catch {
    return false;
  }
}

export function useLeadConnectorFormTracking() {
  useEffect(() => {
    const fired = new Map<string, number>();
    const DEDUPE_WINDOW_MS = 5000;

    const handleMessage = (event: MessageEvent) => {
      if (!isLeadConnectorOrigin(event.origin)) return;

      const data = event.data;
      const dataStr =
        typeof data === 'string'
          ? data
          : (() => {
              try {
                return JSON.stringify(data);
              } catch {
                return '';
              }
            })();

      if (!dataStr || dataStr.length > 50000) return;

      const isSubmitEvent =
        /\b(form[-_ ]?submit(?:ted|ed)?|onFormSubmit|formSubmitted|submission[-_ ]?(?:success|complete|received))\b/i.test(
          dataStr,
        ) || /"(?:event|type|action)"\s*:\s*"(?:form[-_ ]?submit|submit|formSubmitted|form-submit-callback)"/i.test(
          dataStr,
        );

      if (!isSubmitEvent) return;

      const formIdMatch = dataStr.match(/"formId"\s*:\s*"([^"]+)"/i);
      const formId = formIdMatch?.[1] || 'unknown';

      const dedupeKey = formId;
      const now = Date.now();
      const last = fired.get(dedupeKey);
      if (last && now - last < DEDUPE_WINDOW_MS) return;
      fired.set(dedupeKey, now);

      const formName = `LeadConnector Form (${formId})`;
      trackPixelLead({ form_name: formName, form_id: formId });
      trackPixelContact({ form_name: formName, form_id: formId });
      trackFormSubmit(formName, true);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
}

export function usePhoneCallTracking() {
  useEffect(() => {
    const fired = new Map<string, number>();
    const DEDUPE_WINDOW_MS = 2000;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest('a[href^="tel:"]') as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute('href') || '';
      const phoneNumber = href.replace(/^tel:/i, '').trim();
      if (!phoneNumber) return;

      const now = Date.now();
      const last = fired.get(phoneNumber);
      if (last && now - last < DEDUPE_WINDOW_MS) return;
      fired.set(phoneNumber, now);

      const location = (() => {
        if (anchor.closest('header')) return 'header';
        if (anchor.closest('footer')) return 'footer';
        if (anchor.closest('nav')) return 'navigation';
        if (anchor.closest('[data-hero]')) return 'hero';
        return 'main';
      })();

      trackPixelLead({ phone_number: phoneNumber, click_location: location });
      trackPixelContact({ phone_number: phoneNumber, click_location: location });

      import('@/lib/analytics').then(({ trackEvent }) => {
        trackEvent('phone_call_click', {
          phone_number: phoneNumber,
          click_location: location,
        });
      });
    };

    document.addEventListener('click', handleClick, { capture: true });
    return () => document.removeEventListener('click', handleClick, { capture: true });
  }, []);
}

export function useAnalytics() {
  usePageTracking();
  useScrollTracking();
  useClickTracking();
  useLeadConnectorFormTracking();
  usePhoneCallTracking();
}

export function useTrackEvent() {
  return useCallback((eventName: string, params?: Record<string, string | number | boolean>) => {
    import('@/lib/analytics').then(({ trackEvent }) => {
      trackEvent(eventName, params);
    });
  }, []);
}
